<template>
  <div class="MapToolsPanel">
    <header class="MapToolsPanel__header">
      <button
        type="button"
        class="MapToolsPanel__backBtn"
        :title="$t('geoportal.mapTools.close', 'Fermer')"
        :aria-label="$t('geoportal.mapTools.close', 'Fermer')"
        @click="onBack"
      >
        <Icon
          icon="material-symbols-light:chevron-left-rounded"
          class="MapToolsPanel__backIcon"
        />
      </button>
      <h2 class="MapToolsPanel__title">{{ $t('geoportal.mapTools.title', 'ANNOTER LA CARTE') }}</h2>
    </header>
    <div class="MapToolsPanel__body">
      <p v-if="!drawApi" class="MapToolsPanel__unavailable">
        {{ $t('geoportal.mapTools.mapUnavailable', 'Carte non disponible.') }}
      </p>
      <template v-else>
        <section class="MapToolsPanel__section">
          <h3 class="MapToolsPanel__sectionTitle">
            {{ $t('geoportal.mapTools.creationTools', 'OUTILS DE CRÉATION') }}
          </h3>
          <div class="MapToolsPanel__toolRow">
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'draw_point' }"
              :title="$t('geoportal.mapTools.point', 'Point')"
              :aria-label="$t('geoportal.mapTools.point', 'Point')"
              @click="setMode('draw_point')"
            >
              <Icon icon="material-symbols-light:location-on" class="MapToolsPanel__toolIcon" />
            </button>
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'draw_line_string' }"
              :title="$t('geoportal.mapTools.line', 'Ligne')"
              :aria-label="$t('geoportal.mapTools.line', 'Ligne')"
              @click="setMode('draw_line_string')"
            >
              <Icon icon="material-symbols-light:show-chart" class="MapToolsPanel__toolIcon" />
            </button>
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'draw_polygon' }"
              :title="$t('geoportal.mapTools.polygon', 'Polygone')"
              :aria-label="$t('geoportal.mapTools.polygon', 'Polygone')"
              @click="setMode('draw_polygon')"
            >
              <Icon icon="material-symbols-light:hexagon" class="MapToolsPanel__toolIcon" />
            </button>
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'rectangle' }"
              :title="$t('geoportal.mapTools.rectangle', 'Rectangle')"
              :aria-label="$t('geoportal.mapTools.rectangle', 'Rectangle')"
              @click="setMode('rectangle')"
            >
              <Icon icon="material-symbols-light:crop-square" class="MapToolsPanel__toolIcon" />
            </button>
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'circle' }"
              :title="$t('geoportal.mapTools.circle', 'Cercle')"
              :aria-label="$t('geoportal.mapTools.circle', 'Cercle')"
              @click="setMode('circle')"
            >
              <Icon icon="material-symbols-light:radio-button-unchecked" class="MapToolsPanel__toolIcon" />
            </button>
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'freehand' }"
              :title="$t('geoportal.mapTools.freehand', 'Main levée')"
              :aria-label="$t('geoportal.mapTools.freehand', 'Main levée')"
              @click="setMode('freehand')"
            >
              <Icon icon="material-symbols-light:gesture" class="MapToolsPanel__toolIcon" />
            </button>
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'angled-rectangle' }"
              :title="$t('geoportal.mapTools.angledRectangle', 'Rectangle anglé')"
              :aria-label="$t('geoportal.mapTools.angledRectangle', 'Rectangle anglé')"
              @click="setMode('angled-rectangle')"
            >
              <Icon icon="material-symbols-light:aspect-ratio" class="MapToolsPanel__toolIcon" />
            </button>
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'sector' }"
              :title="$t('geoportal.mapTools.sector', 'Secteur')"
              :aria-label="$t('geoportal.mapTools.sector', 'Secteur')"
              @click="setMode('sector')"
            >
              <Icon icon="material-symbols-light:pie-chart" class="MapToolsPanel__toolIcon" />
            </button>
          </div>
        </section>
        <section class="MapToolsPanel__section">
          <h3 class="MapToolsPanel__sectionTitle">
            {{ $t('geoportal.mapTools.measureTools', 'MESURES') }}
          </h3>
          <div class="MapToolsPanel__toolRow">
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'measure_distance' }"
              :title="$t('geoportal.mapTools.measureDistance', 'Mesurer une distance')"
              :aria-label="$t('geoportal.mapTools.measureDistance', 'Mesurer une distance')"
              @click="startMeasureDistance"
            >
              <Icon icon="material-symbols-light:straighten" class="MapToolsPanel__toolIcon" />
            </button>
            <button
              type="button"
              class="MapToolsPanel__toolBtn"
              :class="{ 'MapToolsPanel__toolBtn--active': currentMode === 'measure_area' }"
              :title="$t('geoportal.mapTools.measureArea', 'Mesurer une surface')"
              :aria-label="$t('geoportal.mapTools.measureArea', 'Mesurer une surface')"
              @click="startMeasureArea"
            >
              <Icon icon="material-symbols-light:square-foot" class="MapToolsPanel__toolIcon" />
            </button>
          </div>
        </section>
        <section v-if="areaKm2 != null || areaM2 != null" class="MapToolsPanel__areaSection">
          <p class="MapToolsPanel__areaLabel">{{ $t('geoportal.mapTools.area', 'Aire') }}</p>
          <p v-if="areaM2 != null" class="MapToolsPanel__areaValue MapToolsPanel__areaValue--m2">
            {{ areaM2 }} m²
          </p>
          <p v-if="areaKm2 != null" class="MapToolsPanel__areaValue">{{ areaKm2 }} km²</p>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import Icon from '@/components/ui/Icon.vue'
import { useMyMapStore } from '@/stores/myMapStore'
import { computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue'

const emit = defineEmits<{
  back: []
}>()

const mapStore = useMyMapStore()
const drawApi = computed(() => mapStore.mapDrawApi)

function ensureActivated() {
  drawApi.value?.activate?.()
}

onMounted(() => {
  nextTick(ensureActivated)
})

watch(drawApi, (api) => {
  if (api) nextTick(ensureActivated)
}, { immediate: true })

onBeforeUnmount(() => {
  drawApi.value?.deactivate?.()
})

const currentMode = computed(() => drawApi.value?.currentDrawMode?.value ?? '')
const areaKm2 = computed(() => drawApi.value?.polygonAreaKm2?.value ?? null)
const areaM2 = computed(() => drawApi.value?.polygonAreaM2?.value ?? null)

function setMode(
  mode:
    | 'draw_polygon'
    | 'draw_line_string'
    | 'draw_point'
    | 'simple_select'
    | 'rectangle'
    | 'circle'
    | 'freehand'
    | 'angled-rectangle'
    | 'sector',
) {
  if (!drawApi.value) return
  if (currentMode.value === mode) {
    drawApi.value.changeMode('simple_select')
    return
  }
  drawApi.value.activate()
  drawApi.value.changeMode(mode)
}

function startMeasureDistance() {
  if (currentMode.value === 'measure_distance') {
    drawApi.value?.stopMeasure?.()
    return
  }
  drawApi.value?.startMeasureDistance?.()
}

function startMeasureArea() {
  if (currentMode.value === 'measure_area') {
    drawApi.value?.stopMeasure?.()
    return
  }
  drawApi.value?.startMeasureArea?.()
}

function onBack() {
  drawApi.value?.stopMeasure?.()
  drawApi.value?.deactivate?.()
  emit('back')
}
</script>

<style lang="scss">
.MapToolsPanel {
  display: flex;
  flex-direction: column;
  width: 23.5rem;
  min-width: 0;
  height: 100%;
  background: rgb(var(--v-theme-surface));
  border-radius: 0 0 0 0.375rem;
  overflow: hidden;
}

.MapToolsPanel__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  padding: 0.75rem 1rem;
  background: rgb(var(--v-theme-main-blue));
  color: white;
}

.MapToolsPanel__backBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.MapToolsPanel__backIcon {
  width: 1.5rem;
  height: 1.5rem;
}

.MapToolsPanel__title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.MapToolsPanel__body {
  flex: 1;
  padding: 1rem;
  overflow: auto;
  max-height: calc(100vh - var(--mymap-header-h) - 3.5rem);
}

.MapToolsPanel__unavailable {
  margin: 0;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
}

.MapToolsPanel__section {
  margin-bottom: 1.25rem;
}

.MapToolsPanel__sectionTitle {
  margin: 0 0 0.5rem 0;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-main-blue));
}

.MapToolsPanel__toolRow {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.MapToolsPanel__toolBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(var(--v-theme-main-blue), 0.08);
  color: rgb(var(--v-theme-main-blue));
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.15s ease;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: rgb(var(--v-theme-main-blue));
    color: white;
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-main-blue));
    outline-offset: 2px;
  }

  &--active {
    background: rgb(var(--v-theme-main-blue));
    color: white;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      background: rgb(var(--v-theme-main-blue));
      color: white;
      transform: none;
    }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.MapToolsPanel__toolIcon {
  width: 1.25rem;
  height: 1.25rem;
}

.MapToolsPanel__areaSection {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.MapToolsPanel__areaLabel {
  margin: 0 0 0.25rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgb(var(--v-theme-on-surface-variant));
}

.MapToolsPanel__areaValue {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: rgb(var(--v-theme-main-blue));

  &--m2 {
    font-size: 0.9375rem;
    margin-bottom: 0.25rem;
  }
}


</style>
